import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import type { SlideProps } from '@/components/SlideWizard'
import { copy } from './Slide10Finale.copy'

const ease = [0.16, 1, 0.3, 1] as const

function ShieldCheckIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317-4.66-1.647-8-6.092-8-11.317 0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export default function Slide10Finale(_props: SlideProps) {
  const c = useSlideCopy(copy)
  // The "forward" chevron glyph points into the reading direction.
  const rtl = useLocale() === 'ar'

  return (
    <div className="flex flex-col items-center text-center px-4 mx-auto max-w-3xl">
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-xs sm:text-sm uppercase tracking-[0.25em] text-slate-400 font-semibold"
      >
        {c.eyebrow}
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease }}
        className="mt-4 text-5xl sm:text-7xl font-semibold text-slate-900 tracking-tight leading-[1.05]"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7, ease }}
        className="mt-8 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-2xl"
      >
        {c.body}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.1, ease }}
        className="mt-10 w-full max-w-xl"
      >
        <Link
          to="/tools"
          className="group block rounded-2xl bg-violet-50 px-5 py-4 sm:px-6 sm:py-5 text-start transition-colors hover:bg-violet-100/70"
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-violet-200/70 text-violet-600">
              <ShieldCheckIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-base sm:text-lg font-semibold text-slate-900 leading-snug">
                {c.toolkitHeading}
              </p>
              <p className="mt-0.5 text-sm sm:text-base text-slate-600 leading-snug">
                {c.toolkitDescription}
              </p>
            </div>
            <span
              aria-hidden
              className="text-xl text-violet-500 transition-transform group-hover:translate-x-0.5"
            >
              {rtl ? '‹' : '›'}
            </span>
          </div>
          <span className="sr-only">{c.toolkitCta}</span>
        </Link>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.6, ease }}
        className="mt-10 text-xl sm:text-2xl text-slate-900 leading-relaxed max-w-2xl font-medium"
      >
        {c.closing}
      </motion.p>
    </div>
  )
}
